import {
  AIComponent,
  AccessControl,
  Editor,
  EventEmitter,
  getTranslatedText,
  useIntl,
  getHeaderValue,
  fetchApi,
  getAIRephraseKeys,
  TICKET_SOURCE
} from '@nile/components';
import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import TimerActionsFromReply from '@nile/components/src/Containers/NewWorklog/TimerActionsFromReply';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Button,
  Modal,
  TextContainer,
  TextStyle,
  Badge
} from '@superops/ui-design-system';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { Tooltip } from 'react-clone-tippy';
import BannerComponent from '@nile/mangifera/src/Components/BannerComponent';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { APPLY_AI_PROMPT } from '@nile/graphql/src';
import {
  dompurifier,
  useUserMSPCheckHook
} from '@nile/components/src/Utils/helpers';
import styled from 'styled-components';

const Item = styled.div`
  height: 24px;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  margin-right: 8px;
  line-height: 20px;
  margin-bottom: 2px;
  align-items: center;
  background: #f2f5ff;
  border-radius: 12px;
  padding: 2px 8px 2px 1px;
`;

const Delete = styled.div`
  margin-left: 4px;
  cursor: pointer;
  outline: none;
`;

const DEFAULT_TEMPLATE = {
  name: '',
  content: '',
  placeholdersUsed: '',
  mentions: [],
  attachments: [],
  inlineImages: []
};

const TooltipContent = (props) => {
  const { title } = props;
  return (
    <Tooltip
      title={title}
      inertia
      trigger="mouseenter"
      animation="shift"
      animateFill={false}
      className="tooltip"
      position="bottom"
    >
      {props?.children}
    </Tooltip>
  );
};

const GlobalChatBox = (props) => {
  const { chat, toggleActiveChat, updateTab, activeChat, removeChat } = props;

  const templateTimeOutRef = React.useRef(null);

  const clearTemplateTimeOut = () => {
    if (templateTimeOutRef.current) {
      clearTimeout(templateTimeOutRef.current);
    }
  };
  const updateTabTemplateWithTimeOut = (params) => {
    clearTemplateTimeOut();

    templateTimeOutRef.current = setTimeout(() => {
      updateTab(params);
    }, 100);
  };

  const DEFAULT_TEMPLATE = {
    name: '',
    content: '',
    placeholdersUsed: '',
    mentions: [],
    attachments: [],
    inlineImages: []
  };

  const intl = useIntl();
  const flags = useFlags();
  const subject = chat?.subject;
  const isChatActive = chat?.id === activeChat;
  const [template, setTemplate] = useState({ ...chat?.template });
  const editorRef = React.useRef(null);
  const toUsers = chat?.toUsers || null;
  const ccUsers = chat?.ccUsers?.length ? chat?.ccUsers : [];
  const bccUsers = chat?.bccUsers?.length ? chat?.bccUsers : [];
  const toUsersConfig = chat?.toUsersConfig || {};
  const ccUsersConfig = chat?.ccUsersConfig || {};
  const bccUsersConfig = chat?.bccUsersConfig || {};
  const formatData = chat?.formatData;
  const showWorklog = chat?.showWorklog;
  const workItem = chat?.workItem;
  const source = chat?.data?.source;
  const recipientsConfig = chat?.recipientsConfig || {};
  let conversationScope = chat?.conversationScope;
  const isMSP = useUserMSPCheckHook();
  const [isPrivateChannel, setIsPrivateChannel] = useState(
    conversationScope === 'PRIVATE'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showCC, setShowCC] = useState(ccUsers?.length > 0);
  const [showBcc, setShowBcc] = useState(bccUsers?.length > 0);
  const [recipients, setRecipients] = useState(chat?.recipients || []);

  useEffect(() => {
    setRecipients(chat?.recipients || []);
  }, [chat?.recipients]);

  const [isFileUploading, setIsFileUploading] = useState(false);

  const updateCCUsers = (updatedUsers) => {
    const updatedCCUsers = updatedUsers || [];
    const updatedData = { ...chat, ccUsers: updatedCCUsers };
    updateTab(updatedData);
  };

  const removeReceiver = (data) => {
    setRecipients((prev) => prev.filter((item) => item.value !== data.value));
  };

  const customMultiValueComponent = (option) => {
    const { data, innerRef, innerProps } = option;
    return (
      <div ref={innerRef} {...innerProps} className="flex center m4 ptr">
        <Item>
          <Avatar
            name={data?.label}
            initials={data?.label[0]}
            size="extraSmall"
          />
          <div className="ml-5">{data?.label}</div>
          {!data?.isFixed && (
            <Delete
              className="multi-text-delete"
              tabIndex="-1"
              role="button"
              onClick={() => removeReceiver(data)}
            >
              <Button
                plain
                icon={
                  <div className="fx center">
                    <SVGIcon name="Close" color="black" size="xs" />
                  </div>
                }
              />
            </Delete>
          )}
        </Item>
      </div>
    );
  };
  const CustomRecipient = (props) => {
    const { label } = props;
    return (
      <div className="flex center m4 ptr">
        <Item>
          <Avatar name={label} initials={label?.[0]} size="extraSmall" />
          <div className="ml-5">{label}</div>
        </Item>
      </div>
    );
  };

  const updateBCCUsers = (updatedUsers) => {
    let updatedData = cloneDeep(chat);
    const updatedBCCUsers = updatedUsers || [];
    updatedData = { ...updatedData, bccUsers: updatedBCCUsers };
    updateTab(updatedData);
  };

  const updateRecipients = (updatedUsers) => {
    const updatedRecipients = updatedUsers || [];
    const updatedData = { ...chat, recipients: updatedRecipients };
    updateTab(updatedData);
  };

  const toggleChannel = () => {
    if (!isMSP) {
      return;
    }
    setIsPrivateChannel((prevState) => {
      conversationScope = prevState ? 'PUBLIC' : 'PRIVATE';
      return !prevState;
    });

    updateTab({ ...chat, conversationScope });
  };

  const handleFileAttachments = (files, callback) => {
    const formData = new FormData();
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });

    let updatedData = cloneDeep(chat);
    let updatedTemplate = cloneDeep(template);
    const uploadParams = updatedData?.attachmentUploadParams;
    const attachmentUploadUrl = updatedData?.attachmentUploadUrl;
    for (const property in uploadParams) {
      formData.append(property, uploadParams[property]);
    }
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    setIsFileUploading(true);
    fetchApi(attachmentUploadUrl, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((response) => response.data)
      .then((list) => {
        let updatedAttachments = updatedTemplate?.attachments || [];
        updatedAttachments = [...updatedAttachments, ...list];
        updatedTemplate = {
          ...updatedTemplate,
          attachments: updatedAttachments
        };
        setTemplate(updatedTemplate);
        updatedData = { ...updatedData, template: updatedTemplate };
        updateTabTemplateWithTimeOut(updatedData);
      })
      .catch((error) => {
        console.log('fileUploadError:', error);
      })
      .finally(() => {
        setIsFileUploading(false);
        if (callback) {
          callback();
        }
      });
  };

  const handleAttachmentsDeletion = (fileName) => {
    let updatedData = cloneDeep(chat);
    let updatedTemplate = template;
    const attachments = updatedTemplate?.attachments || [];
    const updatedAttachments = attachments.filter(
      (item) => item.fileName !== fileName
    );
    updatedTemplate = { ...updatedTemplate, attachments: updatedAttachments };
    setTemplate(updatedTemplate);
    updatedData = { ...updatedData, template: updatedTemplate };
    updateTabTemplateWithTimeOut(updatedData);
  };

  const updateTemplate = (updatedTemplate) => {
    let updatedData = { ...chat }; // Spread the tab to update
    const currentTemplate = template || DEFAULT_TEMPLATE; // Get current template
    const updatedTemplateData = { ...currentTemplate, ...updatedTemplate }; // Merge the current template with the update
    setTemplate(updatedTemplateData);
    updatedData = { ...updatedData, template: updatedTemplateData }; // Update the tab with the new template
    if (chat?.onTemplateChange) {
      chat.onTemplateChange(updatedData);
    }
    updateTabTemplateWithTimeOut(updatedData);
  };

  const resetTemplate = (clear) => {
    const updatedTemplate = clear
      ? DEFAULT_TEMPLATE
      : chat?.defaultTemplate || DEFAULT_TEMPLATE;
    editorRef.current.updateEditorContent(updatedTemplate?.content || '');
    updateTemplate(updatedTemplate);
  };

  const sendConversation = () => {
    setIsLoading(true);
    const variables = formatData(chat);
    const client = chat?.mutationClient;
    const mutationAPI = chat?.mutationAPI;
    const callBack = chat?.callBack;
    client
      .mutate({
        mutation: mutationAPI,
        variables: variables
      })
      .then((res) => {
        setIsLoading(false);
        resetTemplate();
        if (callBack) {
          callBack('sucess', res);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        callBack('error', err);
      });
  };

  const minimizeChat = () => {
    toggleActiveChat(chat?.id);
  };

  const toggleCC = () => {
    setShowCC(() => !showCC);
  };

  const toggleBCC = () => {
    setShowBcc(() => !showBcc);
  };

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const toggleCancelModal = () => {
    setShowCancelModal(!showCancelModal);
  };

  const toggleCloseModal = () => {
    setShowCloseModal(!showCloseModal);
  };

  const tabContext = useContext(TabContext);

  const onCloseAction = () => {
    EventEmitter.dispatch('globalConversationClosed', chat);
    removeChat(chat?.id);
  };

  const onClose = () => {
    let hideCloseModal = chat?.hideCloseModal;
    if (chat?.checkHideCloseModal) {
      hideCloseModal = chat?.checkHideCloseModal(chat, tabContext);
    }
    if (hideCloseModal) {
      onCloseAction();
    } else {
      toggleCloseModal();
    }
  };

  const cancelAction = () => {
    if (chat?.onCancel) {
      chat?.onCancel(chat);
    } else {
      resetTemplate(true);
      toggleCancelModal();
    }
  };

  const onCancel = () => {
    if (chat?.hideCancelModal) {
      cancelAction();
    } else {
      toggleCancelModal();
    }
  };

  const onTitleClick = () => {
    chat?.onTitleClick(chat, tabContext);
    minimizeChat();
  };

  const onRephraseCopy = (content, params) => {
    if (editorRef.current?.FroalaEditorRef) {
      EventEmitter.dispatch('insertEditorContent', dompurifier(content));
    } else {
      EventEmitter.dispatch('replaceEditorContent', dompurifier(content));
    }
  };

  const PrimaryAction = chat?.primaryAction;
  const ToolbarContent = chat?.toolbarContent;
  const aiRephraseKeys = getAIRephraseKeys(intl);

  const minimizeTooltip = getTranslatedText(intl, 'common.minimize');
  const maximizeTooltip = getTranslatedText(intl, 'common.maximize');
  const closeTooltip = getTranslatedText(intl, 'common.close');

  useEffect(() => {
    return function cleanup() {
      clearTemplateTimeOut();
    };
  }, []);

  return (
    <div
      key={chat?.id || i}
      className={`global-conversation-box position-relative z400 fhp ${
        isChatActive ? 'active' : ''
      }`}
    >
      <div
        className={`chat-header flex fx-sb actr height50 plr-20 border ${
          isChatActive ? 'bg-white border-bottom-none' : 'background-lavendar'
        }`}
        // onClick={(event) => {
        //   minimizeChat();
        // }}
      >
        <div
          className={`chat-subject pointer ellipsis mr-20 ${
            chat?.onTitleClick ? 'linkText text-medium' : ''
          }`}
          onClick={onTitleClick}
        >
          <TextStyle weight="Medium">{subject}</TextStyle>
        </div>
        <div className="header-actions flex g10 actr">
          {chat?.showAIRephrase && source !== TICKET_SOURCE.Communication && (
            <div>
              <AIComponent
                editorRef={editorRef}
                onCopy={onRephraseCopy}
                type="REPHRASE"
                prompts={chat?.aiPrompts || []}
                data={{
                  Api: APPLY_AI_PROMPT,
                  accessorKey: 'applyAIPrompt'
                }}
                featureName="PROMPT_BUILDER"
                hideButtonText
                headingText={getTranslatedText(intl, 'common.rephrased.text')}
                headingIcon={'AIWand'}
                loadingText={aiRephraseKeys?.loadingText}
                successText={aiRephraseKeys?.successText}
                copyButtonText={getTranslatedText(intl, 'common.insert.text')}
                copySuccessText={getTranslatedText(intl, 'common.inserted')}
                mixpanelEventOnClick={{
                  event: 'Rephrased with prompt - Global ticket conversation',
                  data: {
                    ticketId: chat?.data?.ticketId
                  }
                }}
                mixpanelEventOnCopy={{
                  event:
                    'Copy and pasted rephrased content - Global ticket conversation',
                  data: {
                    ticketId: chat?.data?.ticketId
                  }
                }}
              />
            </div>
          )}

          <TooltipContent
            title={isChatActive ? minimizeTooltip : maximizeTooltip}
          >
            <div onClick={minimizeChat} className="pointer">
              <SVGIcon
                name={isChatActive ? 'MinimiseSmall' : 'MaximisePopup'}
              />
            </div>
          </TooltipContent>

          <TooltipContent title={closeTooltip}>
            <div className="pointer" onClick={onCloseAction}>
              <SVGIcon name="Close" size="sm" />
            </div>
          </TooltipContent>
        </div>
      </div>
      <div
        className={`global-chat-editor-wrapper w100 bg-white pl-15 pr-15 border ${
          isChatActive ? 'pt10 pb10 ' : 'max-height-0'
        }`}
      >
        {source !== TICKET_SOURCE.Communication && (
          <div className="mb20">
            <div className="flex fx-sb actr">
              <div id="copy-mail-users" className="g10 flex center full-width">
                <div className="title">To:</div>
                <div>
                  <TextStyle>
                    {`${toUsers?.firstName} ${toUsers?.lastName || ''} (${
                      toUsers?.email
                    })`}
                  </TextStyle>
                </div>
              </div>
              <div className="cc-bcc-actions flex g10">
                <div className="pointer" onClick={toggleCC}>
                  <TextStyle variation={!showCC && 'subdued'}>CC</TextStyle>
                </div>
                <div className="pointer" onClick={toggleBCC}>
                  <TextStyle variation={!showBcc && 'subdued'}>BCC</TextStyle>
                </div>
              </div>
            </div>

            {showCC && (
              <div className="flex actr mt5">
                <div className="title">CC:</div>
                <div className="w100">
                  <SelectUsers
                    updateField={updateCCUsers}
                    {...ccUsersConfig}
                    value={ccUsers}
                  />
                </div>
              </div>
            )}

            {showBcc && (
              <div className="flex actr mt5">
                <div className="title">BCC:</div>
                <div className="w100">
                  <SelectUsers
                    updateField={updateBCCUsers}
                    {...bccUsersConfig}
                    value={bccUsers}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {source === TICKET_SOURCE.Communication &&
          (chat?.imConfig?.supportedChannelType === 'DM' ? (
            <div className="fx w100 astart">
              <div className="flex tw-gap-x-2 actr">
                <div>
                  <Badge size="sm" status="critical" radial>
                    <SVGIcon name="SuperopsLight" size="sm" />
                  </Badge>
                </div>
                <div>
                  <FormattedMessage id="im.superops.app" />
                </div>
                <div>
                  <CustomRecipient label={chat?.toUsers?.name} />
                </div>
              </div>
            </div>
          ) : (
            <div className="fx w100 astart">
              <div className="fx g5 pt8 pointer" onClick={toggleChannel}>
                <div
                  className={`flex br8 p4 ${
                    !isPrivateChannel ? 'bg-green' : 'bg-grey'
                  }`}
                >
                  <SVGIcon
                    name={
                      !isPrivateChannel
                        ? 'GlobeSharedView'
                        : 'PrivateArticlePlain'
                    }
                    size="sm"
                  />
                </div>

                <FormattedMessage
                  id={
                    !isPrivateChannel
                      ? 'common.public.channel'
                      : 'common.private.channel'
                  }
                />

                {isMSP && (
                  <div className="flex center">
                    <SVGIcon name="ArrowDown" />
                  </div>
                )}
              </div>

              {isPrivateChannel && (
                <div className="w65">
                  <SelectUsers
                    updateField={(val) => updateRecipients(val)}
                    {...recipientsConfig}
                    customMultiValueComponent={customMultiValueComponent}
                    value={recipients}
                  />
                </div>
              )}
            </div>
          ))}

        <Editor
          template={template}
          updateTemplate={updateTemplate}
          module={chat?.module}
          details={chat?.details}
          handleFileAttachments={handleFileAttachments}
          handleAttachmentsDeletion={handleAttachmentsDeletion}
          title="Add Attachments"
          description="Upload/drop files here"
          allowMultiple
          isFileUploading={isFileUploading}
          editorRef={editorRef}
        />

        {showWorklog && (
          <BannerComponent
            bannerKey="globalReplyBanner"
            defaultMessage={getTranslatedText(
              intl,
              'ticket.reply.convert.worklog.info'
            )}
          />
        )}

        {chat?.toolbarContent && <ToolbarContent data={chat} />}

        <div className="flex fx-sb g10 actr mt10">
          <div id="global-conversation-custom-toolbar" className="fg1">
            {showWorklog && (
              <AccessControl
                roleToCheck={{
                  roles: ['tickets.edit', 'serviceCatalog.view']
                }}
                render={
                  <div className="fx actr fg1">
                    <TimerActionsFromReply
                      froalaEditor
                      editorRef={editorRef}
                      workItem={workItem}
                      bannerKey="globalReplyBanner"
                    />
                  </div>
                }
              />
            )}
          </div>
          <div className="flex g10 actr">
            {!chat?.hideCancel && (
              <div>
                <Button plainSecondary onClick={onCancel}>
                  <FormattedMessage id="common.cancel" />
                </Button>
              </div>
            )}
            {PrimaryAction ? (
              <PrimaryAction data={chat} />
            ) : (
              <Button
                icon={<SVGIcon name="SendWhite" color="white" />}
                onClick={sendConversation}
                loading={isLoading}
                primary
              >
                <FormattedMessage id="common.send" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {showCancelModal && (
        <Modal
          activator={<></>}
          open={showCancelModal}
          onClose={toggleCancelModal}
          variant="confirmation"
          title={
            chat?.cancelModalTitle || (
              <FormattedMessage id="app.general.toast.warning" />
            )
          }
          primaryAction={{
            content: chat?.cancelButtonText || (
              <FormattedMessage id="common.delete" />
            ),
            onAction: cancelAction,
            icon: <SVGIcon name="Delete" color="#ffffff" />,
            destructive: true
          }}
          iconSize="xl"
          secondaryActions={[
            {
              content: <FormattedMessage id="common.cancel" />,
              onAction: toggleCancelModal
            }
          ]}
        >
          <TextContainer>
            {chat?.cancelModalContent ? (
              <FormattedMessage id={chat?.cancelModalContent} />
            ) : (
              <FormattedMessage id="ticket.global.conversation.cancel.warning" />
            )}
          </TextContainer>
        </Modal>
      )}

      {showCloseModal && (
        <Modal
          activator={<></>}
          open={showCloseModal}
          onClose={toggleCloseModal}
          variant="confirmation"
          title={
            chat?.closeModalTitle || (
              <FormattedMessage id="app.general.toast.warning" />
            )
          }
          primaryAction={{
            content: chat?.cancelButtonText || (
              <FormattedMessage id="common.confirm" />
            ),
            onAction: onCloseAction
          }}
          iconSize="xl"
          secondaryActions={[
            {
              content: <FormattedMessage id="common.cancel" />,
              onAction: toggleCloseModal
            }
          ]}
        >
          <TextContainer>
            {chat?.cancelModalContent ? (
              <FormattedMessage id={chat?.cancelModalContent} />
            ) : (
              <FormattedMessage id="ticket.global.conversation.cancel.warning" />
            )}
          </TextContainer>
        </Modal>
      )}
    </div>
  );
};

export default GlobalChatBox;
